/**
 * The bar that says "this is a demo".
 *
 * A demo login (demo-mgmt provisions them) is an ordinary account in a real
 * org, seeded with sample data, that is deleted when its time is up. Without a
 * marker a prospect cannot tell sample data from their own, and nobody — the
 * prospect or an operator connected to the account — learns the workspace is
 * about to disappear until the login is refused.
 *
 * The marker is the `demo_expires_at` claim: the post-login Action stamps it on
 * every demo login, and the platform impersonation session carries it too. It is
 * read from the access token, because the shared claim projection does not list
 * it. Renders nothing for any other session, so mounting it is free.
 *
 * Nothing here is a control: the post-login Action refuses a demo login after
 * its expiry and demo-mgmt deletes the environment.
 */
import * as React from "react";
import { FlaskConical } from "lucide-react";
import { claimReader, decodeJwtPayload, type AutheClaims } from "@authe/fe-commons/auth";
import { EXTERNAL_LINKS } from "@/config/constants";

/** Epoch ms of the demo's end, or null for a session that is not a demo. */
export function readDemoExpiry(accessToken: string | null | undefined): number | null {
  const payload = decodeJwtPayload(accessToken);
  // Not part of `AutheClaims`; the reader still resolves it namespace-tolerantly.
  const raw = claimReader.readClaim(payload, "demo_expires_at" as keyof AutheClaims);
  if (typeof raw !== "string" || !raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;

/** "in 3 days" / "in 5 hours" / "in 20 min"; null once it has passed. */
export function demoTimeLeft(expiresAt: number, now: number): string | null {
  const left = expiresAt - now;
  if (left <= 0) return null;
  // Nearest day, like every other "in N days" in the product: a fresh 7-day demo
  // (6 days 23 h left) read "in 6 days" here and "in 7 days" in the QA portal.
  if (left >= 2 * DAY) return `in ${Math.round(left / DAY)} days`;
  if (left >= 2 * HOUR) return `in ${Math.floor(left / HOUR)} hours`;
  if (left >= HOUR) return "in 1 hour";
  return `in ${Math.max(1, Math.ceil(left / 60_000))} min`;
}

function formatEnd(expiresAt: number): string {
  return new Date(expiresAt).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export interface DemoBannerProps {
  accessToken: string | null | undefined;
  /** Clock injection for tests. */
  now?: number;
}

export function DemoBanner({ accessToken, now: fixedNow }: DemoBannerProps): React.ReactElement | null {
  const expiresAt = React.useMemo(() => readDemoExpiry(accessToken), [accessToken]);
  const [tick, setTick] = React.useState(() => Date.now());
  const now = fixedNow ?? tick;
  const bannerRef = React.useRef<HTMLDivElement>(null);

  // A minute is fine-grained enough for a countdown that is shown in days,
  // hours or minutes.
  React.useEffect(() => {
    if (expiresAt === null || fixedNow !== undefined) return;
    const id = window.setInterval(() => setTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [expiresAt, fixedNow]);

  // The shell sizes itself to the viewport minus the bars above it (see
  // RootLayout), so the page does not scroll a second viewport under them.
  React.useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) return;
    const measure = () => document.documentElement.style.setProperty(
      "--demo-banner-height", `${banner.getBoundingClientRect().height}px`,
    );
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(banner);
    return () => { observer?.disconnect(); document.documentElement.style.removeProperty("--demo-banner-height"); };
  }, [expiresAt]);

  if (expiresAt === null) return null;
  const left = demoTimeLeft(expiresAt, now);
  const endingSoon = left !== null && expiresAt - now < DAY;

  return (
    <div
      ref={bannerRef}
      role="status"
      className={[
        "flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1.5 text-xs font-medium print:hidden",
        left === null
          ? "bg-destructive text-destructive-foreground"
          : "bg-brand text-brand-foreground",
      ].join(" ")}
    >
      <FlaskConical className="size-3.5 shrink-0" aria-hidden />
      {left === null ? (
        <span>
          This demo workspace has <strong>ended</strong>. New sign-ins are refused and its sample
          data is being removed.
        </span>
      ) : (
        <span>
          <strong>Demo workspace</strong> with sample data. It ends {left} ({formatEnd(expiresAt)})
          {endingSoon ? " — ask your Authe contact if you need longer." : "."}
        </span>
      )}
      <a
        href={`${EXTERNAL_LINKS.discuss}?topic=pricing`}
        target="_blank"
        rel="noreferrer"
        className="ml-auto underline underline-offset-2 hover:no-underline"
      >
        Talk to us about your own workspace
      </a>
    </div>
  );
}
